"use client";

import { ShoppingCart } from "lucide-react";
import { motion } from "motion/react";
import { useId } from "react";

import { useCart } from "@/components/cart/cart-provider";
import { trackCatalogEvent } from "@/components/catalog/analytics";
import { usePressedInteraction } from "@/components/ui/motion-primitives";
import { useTemporaryFeedback } from "@/components/ui/use-temporary-feedback";
import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";
import type { SessionCartItem } from "@/domain/cart/cart-item";

export interface AddToCartButtonProps {
  readonly product: PublicCatalogProduct;
  readonly quantity?: number;
}

export function AddToCartButton({
  product,
  quantity = 1,
}: Readonly<AddToCartButtonProps>) {
  const { addItem } = useCart();
  const { isVisible: isAdded, showFeedback } = useTemporaryFeedback();
  const {
    isVisible: isOutOfStockMessageVisible,
    showFeedback: showOutOfStockMessage,
  } = useTemporaryFeedback(2500);
  const interaction = usePressedInteraction();
  const isOutOfStock = product.availability === "out_of_stock";
  const outOfStockMessageId = useId();

  return (
    <div className="grid gap-1">
      <motion.button
        aria-describedby={isOutOfStockMessageVisible ? outOfStockMessageId : undefined}
        aria-disabled={isOutOfStock}
        aria-live="polite"
        className={`inline-flex min-h-11 min-w-20 items-center justify-center gap-1 rounded-lg px-3 text-sm font-extrabold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isOutOfStock ? "cursor-not-allowed bg-primary-fixed text-primary" : "bg-primary text-white hover:bg-primary-container"}`}
        onClick={() => {
          if (isOutOfStock) {
            showOutOfStockMessage();
            return;
          }

          addItem(toSessionCartItem(product, quantity), quantity);
          showFeedback();
          trackCatalogEvent({
            eventName: "product_add_to_cart",
            productId: product.id,
            sku: product.sku,
            payload: {
              availability: product.availability,
              brandSlug: product.brandSlug,
              familySlug: product.familySlug,
              productType: product.type,
              quantity,
              source: "catalog_card",
            },
          });
        }}
        type="button"
        {...interaction}
      >
        <ShoppingCart aria-hidden="true" size={16} strokeWidth={2.4} />
        {isAdded ? "Agregado" : "Agregar"}
      </motion.button>
      {isOutOfStockMessageVisible ? (
        <p
          className="text-center text-xs font-semibold text-red-600"
          id={outOfStockMessageId}
          role="alert"
        >
          Este producto no tiene stock disponible.
        </p>
      ) : null}
    </div>
  );
}

export function toSessionCartItem(
  product: PublicCatalogProduct,
  quantity: number,
): SessionCartItem {
  return {
    productId: product.id,
    sku: product.sku,
    name: product.shortName ?? product.name,
    quantity,
    salePrice: product.salePrice,
    wholesalePrice: product.wholesalePrice,
    wholesaleMinQuantity: product.wholesaleMinQuantity,
    currencyCode: product.currencyCode,
    slug: product.slug,
    imageUrl: product.technicalSheetImageUrl ?? product.primaryImageUrl,
    familyName: product.familyName,
    brandName: product.brandName,
    type: product.type,
    availability: product.availability,
  };
}
